import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb } from 'lucide-react';

interface Props {
  titleEn: string; setTitleEn: (v: string) => void;
  titleAm: string; setTitleAm: (v: string) => void;
  descEn: string; setDescEn: (v: string) => void;
  descAm: string; setDescAm: (v: string) => void;
  price: string; setPrice: (v: string) => void;
  currency: string; setCurrency: (v: string) => void;
  areaSqm: string; setAreaSqm: (v: string) => void;
  yearBuilt: string; setYearBuilt: (v: string) => void;
  bedrooms: string; setBedrooms: (v: string) => void;
  bathrooms: string; setBathrooms: (v: string) => void;
  floor: string; setFloor: (v: string) => void;
  totalFloors: string; setTotalFloors: (v: string) => void;
  propertyType: string;
}

export function Step2Details(p: Props) {
  const showRooms = p.propertyType !== 'land' && p.propertyType !== 'commercial';
  const titleLen = p.titleEn.length;
  const descLen = p.descEn.length;
  const titleOver = titleLen > 80;
  const descShort = descLen > 0 && descLen < 200;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Title (English) *</Label>
          <Input value={p.titleEn} onChange={e => p.setTitleEn(e.target.value)} placeholder="e.g. Modern 2BR Apartment in Bole" />
          <div className={`text-[11px] mt-1 ${titleOver ? 'text-destructive' : 'text-muted-foreground'}`}>
            {titleLen}/80 characters
          </div>
        </div>
        <div>
          <Label>Title (Amharic)</Label>
          <Input value={p.titleAm} onChange={e => p.setTitleAm(e.target.value)} placeholder="አማርኛ ርዕስ" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Description (English)</Label>
          <Textarea value={p.descEn} onChange={e => p.setDescEn(e.target.value)} rows={5} placeholder="Describe the property — highlights, neighborhood, condition..." />
          <div className="text-[11px] mt-1 text-muted-foreground">{descLen} characters · Aim for 200+ for best quality score</div>
        </div>
        <div>
          <Label>Description (Amharic)</Label>
          <Textarea value={p.descAm} onChange={e => p.setDescAm(e.target.value)} rows={5} placeholder="ስለ ንብረቱ ይግለጹ..." />
        </div>
      </div>

      {descShort && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs">
          <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <span><strong>Listing tip:</strong> Listings with 200+ character descriptions get up to 35% more inquiries.</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div><Label>Price *</Label><Input type="number" value={p.price} onChange={e => p.setPrice(e.target.value)} placeholder="0" /></div>
        <div>
          <Label>Currency</Label>
          <Select value={p.currency} onValueChange={p.setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ETB">ETB (Birr)</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Area (m²)</Label><Input type="number" value={p.areaSqm} onChange={e => p.setAreaSqm(e.target.value)} placeholder="0" /></div>
        <div><Label>Year Built</Label><Input type="number" value={p.yearBuilt} onChange={e => p.setYearBuilt(e.target.value)} placeholder="2024" /></div>
      </div>

      {showRooms && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label>Bedrooms</Label>
            <Select value={p.bedrooms} onValueChange={p.setBedrooms}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {[...Array(10)].map((_, i) => (<SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>))}
                <SelectItem value="10">10+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bathrooms</Label>
            <Select value={p.bathrooms} onValueChange={p.setBathrooms}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {[...Array(10)].map((_, i) => (<SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>))}
                <SelectItem value="10">10+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Floor</Label><Input type="number" value={p.floor} onChange={e => p.setFloor(e.target.value)} placeholder="0" /></div>
          <div><Label>Total Floors</Label><Input type="number" value={p.totalFloors} onChange={e => p.setTotalFloors(e.target.value)} placeholder="0" /></div>
        </div>
      )}
    </>
  );
}
